import {ChangeDetectorRef, Component, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {ReactiveFormsModule, UntypedFormBuilder, UntypedFormControl, Validators} from '@angular/forms';
import {Constants} from '../../constants';
import {AlertService, AuthService} from '../../services';
import {FormAutofocusDirective} from '../../../shared/directives/form-autofocus.directive';

/**
 * "Change Password" dialog opened from the header profile menu. Self-hosted
 * overlay + panel (not NgbModal) so the panel chrome matches the Pebbl
 * profile-dialog design; the form/API logic is the one that used to live
 * inline in the header.
 */
@Component({
  hostDirectives: [FormAutofocusDirective],
  selector: 'app-change-password',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './change-password.component.html',
  styleUrl: './change-password.component.css'
})
export class ChangePasswordComponent {

  /** signal, not a plain field — open() is invoked from the header's
   *  template, and under zoneless CD a plain write would never re-render
   *  this component (the click only marks the header dirty) */
  isOpen = signal(false);

  changePasswordForm: any;
  /** true while the update request is in flight (footer shows "Updating…") */
  submitted = false;
  /** flips to true on an invalid submit so the field errors become visible */
  isLoading = false;

  showOld = false;
  showNew = false;
  showConfirm = false;

  constructor(
    private fb: UntypedFormBuilder,
    private authenticationService: AuthService,
    private alertService: AlertService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    this.changePasswordForm = this.fb.group({
      old_password: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(25)]],
      new_password: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(25)]],
      confirm_password: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(25), this.passwordMatcher.bind(this)]],
    });
  }

  get cp() {
    return this.changePasswordForm.controls;
  }

  open(): void {
    this.isLoading = false;
    this.submitted = false;
    this.showOld = this.showNew = this.showConfirm = false;
    this.changePasswordForm.reset();
    this.isOpen.set(true);
  }

  close(): void {
    this.isOpen.set(false);
    this.changePasswordForm.reset();
    this.isLoading = false;
    this.submitted = false;
    this.showOld = this.showNew = this.showConfirm = false;
  }

  /** Score 0–4: length ≥ 8, an upper-case letter, a digit, a symbol. */
  get strength(): number {
    const value: string = this.cp.new_password.value || '';
    let score = 0;
    if (value.length >= 8) score++;
    if (/[A-Z]/.test(value)) score++;
    if (/[0-9]/.test(value)) score++;
    if (/[^A-Za-z0-9]/.test(value)) score++;
    return score;
  }

  get strengthLabel(): string {
    return ['Too short', 'Weak', 'Fair', 'Good', 'Strong'][this.strength];
  }

  get strengthColor(): string {
    return ['#E5484D', '#E5484D', '#E8A33D', '#8FBF4D', '#3FA96A'][this.strength];
  }

  /** Live mismatch hint — the validator only re-runs on confirm_password. */
  get mismatch(): boolean {
    const confirm: string = this.cp.confirm_password.value || '';
    return confirm.length > 0 && this.cp.new_password.value !== confirm;
  }

  get canSave(): boolean {
    const oldPassword: string = this.cp.old_password.value || '';
    const newPassword: string = this.cp.new_password.value || '';
    const confirm: string = this.cp.confirm_password.value || '';
    return oldPassword.length > 0 && newPassword.length > 0 && newPassword === confirm;
  }

  private passwordMatcher(control: UntypedFormControl): { [s: string]: boolean } {
    if (this.changePasswordForm && (control.value !== this.changePasswordForm.controls.new_password.value)) {
      return {passwordNotMatch: true};
    }
    return {};
  }

  changePassword() {
    // stop here if form is invalid
    if (this.changePasswordForm.invalid) {
      this.isLoading = true;
      return;
    } else {
      this.isLoading = false;
    }
    this.submitted = true;
    let changePasswordFormData: any = {
      old_password: this.cp.old_password.value,
      password: this.cp.new_password.value,
    };
    this.authenticationService.changePassword(changePasswordFormData).subscribe((data: any) => {
      this.submitted = false;
      this.alertService.success(Constants.SUCCESS_RESET_PASSWORD, 'Success');
      this.close();
      this.changeDetectorRef.markForCheck();
    }, (error: any) => {
      this.submitted = false;
      if (error.status == 422) {
        let self = this;
        if (error.error.data.length > 0) {
          Object.keys(error.error.data).forEach(function (key, data) {
            self.changePasswordForm.controls[error.error.data[key].field].setErrors({'apiError': error.error.data[key].message});
          });
        }
      }
      this.changeDetectorRef.markForCheck();
    });
  }
}
